import { useEffect, useRef, useState } from "react";

type Props = {
  value: string;
  label: string;
  /** What the label becomes for a moment after a successful copy. */
  confirmation?: string;
  className?: string;
  /**
   * The copy actually landed on a clipboard. Fired only then — a denied
   * clipboard says nothing and reports nothing, because a caller counting
   * copies as an action completed must not count one that never happened.
   */
  onCopied?: () => void;
  /**
   * "chip" is a copy that is the row's own action. "quiet" is a copy standing
   * BESIDE a louder one — the share sheet's post rows lead with the composer,
   * and the clipboard is the second way to do the same thing there.
   */
  variant?: "chip" | "quiet";
};

const LOOK = {
  chip:
    "inline-flex h-9 items-center rounded-md border border-border-strong px-3 text-xs font-medium " +
    "text-text-soft transition-colors duration-75 hover:border-border-loud hover:bg-surface " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 " +
    "focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
  quiet:
    "text-xs text-faint underline underline-offset-4 transition-colors duration-75 " +
    "hover:text-text-soft focus-visible:outline-none focus-visible:ring-2 " +
    "focus-visible:ring-focus/70 focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
} as const;

/**
 * Copy confirms by swapping its own label. No toast, no icon animation —
 * the quietest possible acknowledgement that still says it worked.
 */
export function CopyButton({
  value,
  label,
  confirmation = "Copied.",
  className = "",
  onCopied,
  variant = "chip",
}: Props) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return; // clipboard denied — say nothing rather than lie
    }
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1800);
    onCopied?.();
  }

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={label}
      className={`${LOOK[variant]} ${className}`}
    >
      <span className={copied ? "text-accent-ink" : undefined}>
        {copied ? confirmation : label}
      </span>
    </button>
  );
}
